// web/src/lib/deckPipeline.ts
// The deck create pipeline (per-slide playlist). Dependency-injected so its logic
// (call order, two render tiers, voice, client-side schedule, assembly) is unit-tested
// without the pdf.js worker or the network. Sync is STRUCTURAL: each slide is on screen
// exactly while its clip plays — voiceDeck returns per-clip timings, the schedule is
// derived from them deterministically (buildDeckSchedule). No STT inference, no zoom (v1).
import { KEY_SLIDE_SCALE, FILLER_SCALE, SLIDE_IMAGE_TYPE, type RenderedPage } from "./pdf";
import { buildDeckSchedule } from "@/player/slideShow";
import { resolveAudioAssets } from "@/player/audio/director";
import type {
  Mode, Profanity, WordTiming,
  KeySlide, FillerImage, SlideScheduleEntry, DeckVerdict, AudioProfile, AudioAssets,
  Segment, SegmentTiming, DetectedObject,
} from "@/types/roast";
import type { BleepMode } from "@/config/censorship";
import { DEFAULT_BLEEP_MODE } from "@/config/censorship";
import { swapSoundalikes, findUnhandledProfanity } from "@/player/profanity";
import { sanitizeWithLog } from "./sanitizeScript";
import { curateDeckZooms } from "./curateDeckZooms";

export interface DeckPipelineParams {
  file: Blob; uid: string; roastId: string;
  mode: Mode; profanity: Profanity; name: string | null; context: string | null;
  voiceId: string; pageCount: number; sourceType?: "deck" | "doc";
  doodleKeySlides?: boolean;
  bleepMode?: BleepMode;   // "soundalike" ⇒ swap cusses → clean minced-oaths before voiceDeck
}

export interface DeckRaster { slideIndex: number; blob: Blob; doodleBlob?: Blob }

export type DeckStageKey = "upload" | "comprehend" | "render" | "voice" | "schedule" | "done";

export interface DeckPipelineDeps {
  uploadPdf: (uid: string, roastId: string, file: Blob) => Promise<{ pdfPath: string }>;
  comprehendDeck: (req: {
    mode: Mode; profanity: Profanity; name: string | null; context: string | null; pdfPath: string; pageCount: number;
    sourceType?: "deck" | "doc";
  }) => Promise<{ data: {
    segments: Segment[]; title: string; audioProfile: AudioProfile; verdict: DeckVerdict | null;
  } }>;
  renderPages: (file: Blob, indices: number[], scale: number, onPage?: (done: number, total: number) => void) => Promise<RenderedPage[]>;
  voiceDeck: (req: { segments: Segment[]; pageCount: number; mode: Mode; voiceId: string; profanity: Profanity })
    => Promise<{ data: {
      audioBase64: string; mimeType: string; wordTimings: WordTiming[]; durationSec: number;
      script: string; segmentTimings: SegmentTiming[];
    } }>;
  detectDeckRegions?: (req: { slides: { slideIndex: number; imageBase64: string; phrase: string; mimeType?: "image/jpeg" | "image/webp" }[] })
    => Promise<{ data: { regions: Record<number, DetectedObject[]> } }>;
  resolveAudio?: typeof resolveAudioAssets;
  makeObjectUrl?: (blob: Blob) => string;
  onStage?: (stage: DeckStageKey, progress: number) => void;
  /** Surfaces the comprehended script (joined segment text) once, right after comprehend resolves. */
  onScript?: (text: string) => void;
  /** Cumulative per-slide render progress across BOTH tiers (tentpole then sweep):
   *  done climbs 1..totalToRender monotonically, total is constant — never per-pass. */
  onRenderProgress?: (done: number, total: number) => void;
  /** Per-stage wall-clock (ms), emitted once at the end. Defaults to console.info("[deck-perf]", …). */
  onTiming?: (timings: Record<string, number>) => void;
  /** Optional per-slide comedic doodle (the generateDoodle callable). */
  doodleSlide?: (req: { image: string; mimeType: string; mode: Mode; script?: string })
    => Promise<{ data: { imageBase64: string; mimeType: string } }>;
}

export interface DeckPipelineResult {
  pdfPath: string; script: string; title: string; verdict: DeckVerdict | null;
  keySlides: KeySlide[]; fillerImages: FillerImage[]; slideSchedule: SlideScheduleEntry[];
  wordTimings: WordTiming[]; durationSec: number; audioBase64: string; mimeType: string;
  audio: AudioAssets; keyRasters: DeckRaster[]; fillerRasters: DeckRaster[];
}

/** comprehendDeck occasionally 500s on a transient empty/truncated/safety-blocked model
 *  response (intermittent — the same deck can fail then succeed). Re-roll a bounded number
 *  of times; a fresh generation almost always parses. */
const COMPREHEND_ATTEMPTS = 3;
function isRetryableComprehendError(e: unknown): boolean {
  // Don't waste re-rolls on deterministic client errors; retry transient/internal failures
  // (the empty/truncated/safety 500 surfaces as a Functions error code of "internal").
  const code = String((e as { code?: unknown } | null)?.code ?? "");
  return !/invalid-argument|permission-denied|unauthenticated|not-found/.test(code);
}
async function withComprehendRetry<T>(run: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await run();
    } catch (e) {
      if (attempt >= COMPREHEND_ATTEMPTS || !isRetryableComprehendError(e)) throw e;
      console.warn(`[deck] comprehend attempt ${attempt}/${COMPREHEND_ATTEMPTS} failed — retrying`, e);
    }
  }
}

// Slide doodles (opt-in, see DeckPipelineParams.doodleKeySlides): capped slide count and a
// hard per-call timeout so a slow/stuck model call can never stall the whole pipeline —
// it's generated inside the same parallel block as voiceDeck, so it must not be the long pole.
const DOODLE_TIMEOUT_MS = 45_000;
const DOODLE_MAX_SLIDES = 3;
function b64ToBlob(b64: string, type: string): Blob {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type });
}

export async function runDeckPipeline(
  params: DeckPipelineParams, deps: DeckPipelineDeps,
): Promise<DeckPipelineResult> {
  const resolveAudio = deps.resolveAudio ?? resolveAudioAssets;
  const makeObjectUrl = deps.makeObjectUrl ?? ((b: Blob) => URL.createObjectURL(b));
  const stage = deps.onStage ?? (() => {});
  const emitTiming = deps.onTiming ?? ((t: Record<string, number>) => console.info("[deck-perf]", t));
  const clock = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
  const perf: Record<string, number> = {};
  const tStart = clock();
  let tMark = tStart;
  const lap = (key: string) => { const n = clock(); perf[key] = Math.round(n - tMark); tMark = n; };
  const { file, uid, roastId, mode, profanity, name, context, voiceId, pageCount, sourceType, doodleKeySlides } = params;

  // 1) stage the PDF (comprehendDeck downloads it server-side)
  stage("upload", 10);
  const { pdfPath } = await deps.uploadPdf(uid, roastId, file);
  lap("upload");

  // 2) comprehend the whole deck → ordered segments + title + audioProfile + verdict.
  // Bounded retry (see withComprehendRetry): comprehend occasionally returns a transient 500.
  stage("comprehend", 25);
  const c = (await withComprehendRetry(
    () => deps.comprehendDeck({ mode, profanity, name, context, pdfPath, pageCount, sourceType }),
  )).data;
  // Pre-TTS text prep on each segment (the ONE source string per clip): sanitizeScript strips
  // model self-check leaks ("[1] … profanity counts …") deterministically, then the soundalike
  // swap (when bleepMode==="soundalike") replaces cusses with clean minced-oaths — BEFORE they
  // feed onScript + zoom detection + voiceDeck, so audio and alignment tokens stay consistent.
  const soundalike = (params.bleepMode ?? DEFAULT_BLEEP_MODE) === "soundalike";
  const prep = (s: string, label: string): string => {
    const clean = sanitizeWithLog(s, label);
    return soundalike ? swapSoundalikes(clean) : clean;
  };
  const segments: Segment[] = c.segments.map((s, i) => ({
    ...s,
    text: prep(s.text, `deck segment ${i}`),
    ...(s.zoomPhrase ? { zoomPhrase: prep(s.zoomPhrase, `deck zoomPhrase ${i}`) } : {}),
  }));
  // Breadcrumb: real curse stems that survived the swap (no clean stand-in) — still bleeped
  // via stem detection; surfaced so the tables grow from evidence.
  if (soundalike) {
    const unhandled = findUnhandledProfanity(segments.map((s) => s.text).join(" "));
    if (unhandled.length) console.warn("[censor] spoken-but-bleeped (no clean stand-in):", unhandled);
  }
  deps.onScript?.(segments.map((s) => s.text).join(" "));
  lap("comprehend");

  // Two render tiers from the segment roles: tentpoles (single-slide segments) HELD
  // full-frame → high scale; sweep slides (multi-slide runs) only flash by → low scale.
  const inRange = (i: number) => Number.isInteger(i) && i >= 0 && i < pageCount;
  const tentpoleIndices = Array.from(new Set<number>(
    segments.filter((s) => s.slideIndices.length === 1).flatMap((s) => s.slideIndices),
  )).filter(inRange).sort((a, b) => a - b);
  const tentSet = new Set(tentpoleIndices);
  const sweepIndices = Array.from(new Set<number>(
    segments.filter((s) => s.slideIndices.length > 1).flatMap((s) => s.slideIndices),
  )).filter((i) => inRange(i) && !tentSet.has(i)).sort((a, b) => a - b);

  // 3) render the two tiers — only what we show. Both passes feed ONE cumulative counter
  // (rendered/totalToRender) via onRenderProgress so the "N of M" caption climbs slide-by-slide
  // across the tier boundary and never resets backward. stage("render") marks the phase once.
  stage("render", 50);
  const totalToRender = tentpoleIndices.length + sweepIndices.length;
  let rendered = 0;
  const onRenderPage = () => { rendered++; deps.onRenderProgress?.(rendered, totalToRender); };
  const tentpoleRendered = await deps.renderPages(file, tentpoleIndices, KEY_SLIDE_SCALE, onRenderPage);
  lap("renderKey");
  const sweepRendered = await deps.renderPages(file, sweepIndices, FILLER_SCALE, onRenderPage);
  lap("renderFiller");
  const keyRasters: DeckRaster[] = tentpoleRendered.map((p) => ({ slideIndex: p.slideIndex, blob: p.blob }));
  const fillerRasters: DeckRaster[] = sweepRendered.map((p) => ({ slideIndex: p.slideIndex, blob: p.blob }));

  // 4) synthesize voice AND detect zoom regions — independent, so run them together.
  stage("voice", 70);
  const eligibleZoom = segments.filter(
    (s) => s.zoomPhrase && s.slideIndices.length === 1 && tentSet.has(s.slideIndices[0]),
  );
  const rasterBySlide = new Map(keyRasters.map((r) => [r.slideIndex, r.blob]));
  const detect = async (): Promise<Record<number, DetectedObject[]>> => {
    if (!deps.detectDeckRegions || eligibleZoom.length === 0) return {};
    try {
      const slides = await Promise.all(
        eligibleZoom.map(async (s) => ({
          slideIndex: s.slideIndices[0],
          imageBase64: await blobToBase64(rasterBySlide.get(s.slideIndices[0])!),
          phrase: s.zoomPhrase!,
          mimeType: SLIDE_IMAGE_TYPE,
        })),
      );
      return (await deps.detectDeckRegions({ slides })).data.regions;
    } catch (e) {
      console.warn("[deck-zoom] detection failed — no zoom this deck", e);
      return {};
    }
  };
  // Slide doodles (opt-in): first ≤3 tentpoles, each anchored to its segment text.
  // Runs in the same parallel block as voiceDeck → hidden in the voice window.
  const segByTentpole = new Map<number, string>();
  for (const s of segments) {
    if (s.slideIndices.length === 1 && tentSet.has(s.slideIndices[0]) && !segByTentpole.has(s.slideIndices[0])) {
      segByTentpole.set(s.slideIndices[0], s.text);
    }
  }
  const doodleTargets = tentpoleIndices.slice(0, DOODLE_MAX_SLIDES);
  const doodleSlides = async (): Promise<Map<number, { blob: Blob; url: string }>> => {
    const out = new Map<number, { blob: Blob; url: string }>();
    if (!deps.doodleSlide || !doodleKeySlides) return out;
    await Promise.all(doodleTargets.map(async (idx) => {
      const blob = rasterBySlide.get(idx);
      const script = segByTentpole.get(idx);
      if (!blob || !script) return;
      try {
        const image = await blobToBase64(blob);
        let timerId: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<never>((_, rej) => {
          timerId = setTimeout(() => rej(new Error("doodle timeout")), DOODLE_TIMEOUT_MS);
        });
        try {
          const { data } = await Promise.race([deps.doodleSlide!({ image, mimeType: SLIDE_IMAGE_TYPE, mode, script }), timeout]);
          const dBlob = b64ToBlob(data.imageBase64, data.mimeType);
          out.set(idx, { blob: dBlob, url: makeObjectUrl(dBlob) });
        } finally {
          if (timerId !== undefined) clearTimeout(timerId);
        }
      } catch (e) {
        console.warn(`[deck-doodle] slide ${idx} doodle failed — using original`, e);
      }
    }));
    return out;
  };

  const [v, rawRegions, doodleMap] = await Promise.all([
    deps.voiceDeck({ segments, pageCount, mode, voiceId, profanity }).then((r) => r.data),
    detect(),
    doodleSlides(),
  ]);
  lap("voice");
  // [voice] breadcrumb (detection only — the fix lives in protected functions/): a segment
  // whose aligned words start deep into its clip means the TTS vocalized audio that is NOT
  // in the script (observed 2026-07-05: Gemini-TTS narrating its director-notes prompt).
  // The spoken preamble is invisible to captions/alignment, so only this clip-vs-words
  // duration mismatch betrays it at generation time.
  const SEG_LEAD_MAX_SEC = 2.5;
  for (const st of v.segmentTimings) {
    const starts = v.wordTimings.filter((w) => w.start >= st.clipStartSec && w.start < st.clipEndSec).map((w) => w.start);
    if (starts.length === 0) continue;
    const lead = Math.min(...starts) - st.clipStartSec;
    if (lead > SEG_LEAD_MAX_SEC) {
      console.warn(
        `[voice] segment for slides [${st.slideIndices.join(",")}]: first word ${lead.toFixed(1)}s into a ${(st.clipEndSec - st.clipStartSec).toFixed(1)}s clip — TTS may have spoken non-script audio (prompt narration?)`,
      );
    }
  }
  for (const r of keyRasters) { const d = doodleMap.get(r.slideIndex); if (d) r.doodleBlob = d.blob; }

  // 5) build the deterministic flip-through schedule CLIENT-SIDE from the clip timings.
  stage("schedule", 90);
  const realizedSlideIndices = Array.from(new Set<number>([
    ...keyRasters.map((r) => r.slideIndex),
    ...fillerRasters.map((r) => r.slideIndex),
  ])).sort((a, b) => a - b);
  const slideSchedule = buildDeckSchedule(v.segmentTimings, realizedSlideIndices);

  // 5b) curate the zoom regions against the dwell sequence (anti-jerk caps).
  const dwellOrder = slideSchedule.filter((e) => e.kind === "dwell").map((e) => e.slideIndex);
  const curated = curateDeckZooms(rawRegions, dwellOrder);

  // 6) assemble (object URLs for preview; imagePath filled at save).
  const keySlides: KeySlide[] = keyRasters.map((r) => {
    const d = doodleMap.get(r.slideIndex);
    return {
      slideIndex: r.slideIndex, imageUrl: makeObjectUrl(r.blob), imagePath: "",
      detectedObjects: curated[r.slideIndex] ?? [],
      ...(d ? { doodleImageUrl: d.url } : {}),
    };
  });
  const fillerImages: FillerImage[] = fillerRasters.map((r) => ({
    slideIndex: r.slideIndex, imageUrl: makeObjectUrl(r.blob), imagePath: "",
  }));
  // Deck path: within-segment monotonic cursor so a repeated trigger phrase resolves to its
  // correct later occurrence. The photo path (StudioCreate) omits this opt → global-first.
  const audio = resolveAudio(c.audioProfile, v.script, v.wordTimings, mode, roastId, { withinSegmentCursor: true });

  lap("assemble");
  perf.total = Math.round(clock() - tStart);
  emitTiming(perf);

  stage("done", 100);
  return {
    pdfPath, script: v.script,
    title: prep(c.title, "deck title"),
    verdict: c.verdict ? { ...c.verdict, line: prep(c.verdict.line, "deck verdict") } : null,
    keySlides, fillerImages, slideSchedule,
    wordTimings: v.wordTimings, durationSec: v.durationSec,
    audioBase64: v.audioBase64, mimeType: v.mimeType, audio, keyRasters, fillerRasters,
  };
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}
